import type React from "react";
import "./Table.scss";
import clsx from "clsx";
import styled from "styled-components";

interface TableProps extends React.ComponentProps<"table"> {
  striped?: boolean;
  headerBgColor?: string;
  headerTextColor?: string;
  mainRowBgColor?: string;
  mainRowTextColor?: string;
  altRowBgColor?: string;
  altRowTextColor?: string;
  borderColor?: string;
  maxWidth?: string;
}

interface TableHeadProps extends React.ComponentProps<"th"> {
  maxWidth?: string;
}

interface TableCellProps extends React.ComponentProps<"td"> {
  maxWidth?: string;
}

const StyledTable = styled.table<{
  $headerBgColor: string;
  $headerTextColor: string;
  $mainRowBgColor: string;
  $mainRowTextColor: string;
  $altRowBgColor: string;
  $altRowTextColor: string;
  $borderColor: string;
  $striped: boolean;
  $maxWidth?: string;
}>`
  max-width: ${(props) => props.$maxWidth || "none"};

  thead {
    background-color: ${(props) => props.$headerBgColor};
    color: ${(props) => props.$headerTextColor};
  }

  tbody tr {
    background-color: ${(props) => props.$mainRowBgColor};
    color: ${(props) => props.$mainRowTextColor};
  }

  th,
  td {
    border: 1px solid ${(props) => props.$borderColor};
  }

  ${(props) =>
    props.$striped &&
    `
    tbody tr:nth-child(even) {
      background-color: ${props.$altRowBgColor};
      color: ${props.$altRowTextColor};
    }
  `}

  @media screen and (max-width: 768px) {
    max-width: none;

    tbody tr {
      border: 1px solid ${(props) => props.$borderColor};
    }

    td {
      border-bottom: 1px solid ${(props) => props.$borderColor};
    }

    td::before {
      background-color: ${(props) => props.$headerBgColor};
      color: ${(props) => props.$headerTextColor};
      border-right: 1px solid ${(props) => props.$borderColor};
    }

    ${(props) =>
      props.$striped &&
      `
      tbody tr:nth-child(even) td::before {
        background-color: ${props.$headerBgColor};
        color: ${props.$headerTextColor};
      }
    `}
  }
`;

const StyledTableCell = styled.td<{
  $maxWidth?: string;
}>`
  max-width: ${(props) => props.$maxWidth || "none"};

  @media screen and (max-width: 768px) {
    max-width: none;
  }
`;

const Table = ({
  className,
  striped = false,
  headerBgColor = 'var(--datatable-variantdefault-statedefault-headerbgcolor)',
  headerTextColor = 'var(--datatable-variantdefault-statedefault-headerfgcolor)',
  mainRowBgColor = 'var(--datatable-variantdefault-statedefault-oddrowbgcolor)',
  mainRowTextColor = 'var(--datatable-variantdefault-statedefault-oddrowfgcolor)',
  altRowBgColor = 'var(--datatable-variantdefault-statedefault-evenrowbgcolor)',
  altRowTextColor = 'var(--datatable-variantdefault-statedefault-evenrowfgcolor)',
  borderColor = 'var(--datatable-variantdefault-statedefault-bordercolor)',
  maxWidth,
  ...props
}: TableProps) => {
  return (
    <div className="table-container table-responsive">
      <StyledTable
        className={clsx(className)}
        $headerBgColor={headerBgColor}
        $headerTextColor={headerTextColor}
        $mainRowBgColor={mainRowBgColor}
        $mainRowTextColor={mainRowTextColor}
        $altRowBgColor={altRowBgColor}
        $altRowTextColor={altRowTextColor}
        $borderColor={borderColor}
        $striped={striped}
        $maxWidth={maxWidth}
        {...props}
      />
    </div>
  );
};

const TableHeader = ({
  className,
  ...props
}: React.ComponentProps<"thead">) => {
  return <thead className={className} {...props} />;
};

const TableBody = ({ className, ...props }: React.ComponentProps<"tbody">) => {
  return <tbody className={className} {...props} />;
};

const TableRow = ({ className, ...props }: React.ComponentProps<"tr">) => {
  return <tr className={className} {...props} />;
};

const TableHead = ({
  className,
  maxWidth,
  style,
  ...props
}: TableHeadProps) => {
  return (
    <th
      className={clsx("table-head", className)}
      style={{ maxWidth, ...style }}
      {...props}
    />
  );
};

const TableCell = ({
  className,
  maxWidth,
  style,
  ...props
}: TableCellProps) => {
  return (
    <StyledTableCell
      className={clsx("table-cell", className)}
      $maxWidth={maxWidth}
      style={style}
      {...props}
    />
  );
};

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
